/**
 * useTracking — Connects the TrackingService to React app state.
 *
 * Call once at app root level (App.tsx). The hook:
 *   1. Initializes the tracker when config + consent are both known.
 *   2. Re-syncs consent when the user changes cookie settings mid-session.
 *   3. Manages the session-duration timer (performance consent required).
 *
 * Session-duration timer behaviour:
 *   - Starts immediately when performance consent is given.
 *   - Pauses on visibilitychange: hidden (tab in background), resumes on visible.
 *   - Sends on tab hidden + beforeunload (both, for mobile + desktop reliability).
 *   - Sessions < 3 s are discarded (bot/bounce filter).
 *   - Uses navigator.sendBeacon → survives tab close reliably.
 *
 * @see packages/brand-audi/src/services/tracking.ts
 * @see docs/ims-brand-frontends/09-cookie-consent-tracking.md#session-dauer-tracking
 */
import { useEffect, useRef } from 'react';
import { useAppStore } from '../store/app.store';
import { tracker, EVENTS } from '../services/tracking';
import type { ResolvedConfig } from '@ims/shared';

interface UseTrackingOptions {
  config: ResolvedConfig | null;
}

export function useTracking({ config }: UseTrackingOptions): void {
  const consentGiven       = useAppStore((s) => s.consentGiven);
  const performanceCookies = useAppStore((s) => s.performanceCookies);
  const marketingCookies   = useAppStore((s) => s.marketingCookies);

  // Tracks whether we have already sent session_init + visit for this session.
  const sessionStartedRef = useRef(false);

  // --------------------------------------------------------------------------
  // Init / consent sync
  // --------------------------------------------------------------------------

  useEffect(() => {
    // Wait for both config and user consent before initializing.
    if (!config || !consentGiven) return;

    tracker.init({
      brandId:     config.brand.id,
      marketId:    config.market.id,
      modelId:     config.model.id,
      performance: performanceCookies,
      marketing:   marketingCookies,
    });

    if (!sessionStartedRef.current) {
      // Fire once per session — session_init (necessary, always) + visit (performance, gated).
      tracker.track(EVENTS.SESSION_INIT);
      tracker.track(EVENTS.VISIT);
      sessionStartedRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, consentGiven]); // intentionally omit performance/marketing — handled below

  // Sync consent changes without re-sending session_init / visit.
  useEffect(() => {
    if (!sessionStartedRef.current) return;
    tracker.updateConsent({ performance: performanceCookies, marketing: marketingCookies });
  }, [performanceCookies, marketingCookies]);

  // --------------------------------------------------------------------------
  // Session-duration timer (performance consent required)
  // --------------------------------------------------------------------------

  const sessionStartRef = useRef<number | null>(null);
  const pauseStartRef   = useRef<number | null>(null);
  const totalPausedRef  = useRef(0);
  const sentRef         = useRef(false);

  useEffect(() => {
    if (!consentGiven || !performanceCookies) return;

    // (Re-)arm the timer — also called when the tab becomes visible again.
    function armTimer() {
      sessionStartRef.current = Date.now();
      totalPausedRef.current  = 0;
      sentRef.current         = false;
    }

    armTimer();

    function sendDuration() {
      if (!sessionStartRef.current || sentRef.current) return;
      const activeMs = Date.now() - sessionStartRef.current - totalPausedRef.current;
      if (activeMs < 3_000) return; // discard bot-bounces < 3 s
      sentRef.current = true;
      tracker.trackSessionDuration({
        duration: Math.floor(activeMs / 1_000),
      });
      sessionStartRef.current = null;
    }

    function onVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        if (sessionStartRef.current) {
          pauseStartRef.current = Date.now();
        }
        sendDuration(); // mobile: beforeunload is unreliable, use visibilitychange
      } else {
        // Tab became visible again — record the pause duration and re-arm.
        if (pauseStartRef.current) {
          totalPausedRef.current += Date.now() - pauseStartRef.current;
          pauseStartRef.current = null;
        }
        armTimer(); // fresh session segment
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('beforeunload', sendDuration); // desktop backup

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('beforeunload', sendDuration);
    };
  }, [consentGiven, performanceCookies]);
}
